<template>
  <section
    id="home"
    class="relative bg-white h-[915px] max-[1199px]:h-[590px] max-[1023px]:h-[470px] max-[767px]:h-auto min-[1440px]:h-[1000px]"
  >
    <LandingHomeRocketBg />
    <div
      class="mx-auto w-full max-w-container h-full flex justify-start items-center"
    >
      <div
        class="relative sm:z-10 mt-80 xl:mt-40 w-3/4 xl:w-1/2 mx-9 max-[767px]:w-full max-[767px]:mx-4 max-[767px]:px-4"
      >
        <h1
          class="text-2xl md:text-4xl w-80 md:w-full md:text-[3.625rem] font-black leading-[1] md:leading-[1.2] mb-[0.35em] text-[#333]"
        >
          <Translate translate-key="page_data.index.h1" />
        </h1>
        <p
          class="text-[1.3125rem] mb-[3.3125rem] max-[1023px]:text-base max-[1023px]:mb-14 m-0 text-[#666]"
        >
          <Translate translate-key="landing_home.content" />
        </p>
        <ul
          class="flex flex-wrap w-68 ml-0 md:w-full flex-col max-[767px]:w-full md:flex-row justify-start gap-2 md:gap-8 list-none p-0 m-0 mb-[105px] max-[1023px]:mb-14"
        >
          <li v-if="!isAuthenticated" class="max-[767px]:w-full">
            <Button
              id="getStarted"
              as="a"
              :href="linkHref('/signup')"
              variant="landing1"
              size="xl"
              class="max-[767px]:w-full max-[767px]:justify-center"
            >
              <Translate translate-key="landing_home.get_started" />
            </Button>
          </li>
          <li v-else-if="isCardholder" class="max-[767px]:w-full">
            <Button
              id="getStarted"
              :as="NuxtLink"
              :to="'/cardholder/card'"
              variant="landing1"
              size="xl"
              class="max-[767px]:w-full max-[767px]:justify-center"
            >
              <Translate translate-key="landing_home.get_started" />
            </Button>
          </li>
          <li v-else class="max-[767px]:w-full">
            <Button
              id="getStarted"
              as="a"
              :href="linkHref('/tradelines-for-sale?limit=50&offset=0')"
              variant="landing1"
              size="xl"
              class="max-[767px]:w-full max-[767px]:justify-center"
            >
              <Translate translate-key="landing_home.get_started" />
            </Button>
          </li>
          <li class="max-[767px]:w-full">
            <Button
              id="helpToChoose"
              variant="landingOutline"
              size="xl"
              class="max-[767px]:w-full max-[767px]:justify-center"
              @click="showQuizzModal"
            >
              <Translate translate-key="landing_home.calculator" />
            </Button>
          </li>
        </ul>
        <SocialLinks />
      </div>
    </div>
    <div class="absolute bottom-0 flex justify-center items-center w-full">
      <button
        type="button"
        class="scroll-next-btn h-[38px] w-[33px] border-0 bg-transparent bg-contain bg-center bg-no-repeat cursor-pointer max-[767px]:static max-[767px]:block max-[767px]:translate-x-0 max-[767px]:translate-y-0 max-[767px]:mx-auto max-[767px]:mb-[30px]"
        aria-label="Scroll to next section"
        @click="scrollToOffer"
      >
        <span class="sr-only">
          <Translate translate-key="landing_home.scroll_next" />
        </span>
      </button>
    </div>
  </section>
</template>

<script setup lang="ts">
import { useScroll } from "@vueuse/core";
import { NuxtLink } from "#components";
import Translate from "~/shared/components/i18n/Translate/Translate.vue";
import SocialLinks from "~/shared/components/layout/SocialLinks.vue";
import { Button } from "~/shared/components/ui/button";
import LandingHomeRocketBg from "~/features/landing-page/components/LandingHomeRocketBg.vue";
import { useCurrentUserStore } from "~/stores/userStore";
import { useDialogsStore, Dialogs } from "~/stores/dialogStore";

const { linkHref } = useNativeRouteLink();
const userStore = useCurrentUserStore();
const dialogsStore = useDialogsStore();

const isAuthenticated = computed(() => userStore.isAuthenticated);
const isCardholder = computed(() => userStore.isCardholder);

const { y: scrollY } = useScroll(
  typeof window !== "undefined" ? window : undefined,
  { behavior: "smooth" },
);

let quizzScriptAdded = false;
function showQuizzModal() {
  if (import.meta.client && !quizzScriptAdded) {
    const script = document.createElement("script");
    script.src =
      "https://lq3-production01.s3.amazonaws.com/lead_quizzes_3.0/tracking/js/properties/iiyamlecqtmh1w.js";
    document.head.appendChild(script);
    quizzScriptAdded = true;
  }
  dialogsStore.openDialog(Dialogs.QuizModal);
}

function scrollToOffer() {
  if (import.meta.server) return;
  const el = document.querySelector<HTMLElement>("#offer");
  if (el) {
    // Double rAF so layout read runs after paint (avoids forced reflow in Lighthouse).
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const top = el.getBoundingClientRect().top + window.scrollY;
        scrollY.value = top;
      });
    });
  }
}
</script>

<style scoped>
.scroll-next-btn {
  background-image: url("/images/landing/scroll.png");
}
@media (min-resolution: 192dpi), (min-resolution: 2dppx) {
  .scroll-next-btn {
    background-image: url("/images/landing/scroll@2x.png");
  }
}
@media (min-resolution: 288dpi), (min-resolution: 3dppx) {
  .scroll-next-btn {
    background-image: url("/images/landing/scroll@3x.png");
  }
}
</style>
